import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  acceptAgentPromptCandidate,
  AgentDetail,
  AgentRuntimeFields,
  AgentEvaluationCallDirection,
  AgentEvaluationPersonaType,
  AgentEvaluationScenarioPayload,
  AgentEvaluationPromptCandidate,
  AgentEvaluationRun,
  AgentEvaluationScenario,
  AgentEvaluationSuite,
  AgentEvaluationUnavailableError,
  AgentPromptVersion,
  createAgentEvaluationScenario,
  createAgentPromptOptimization,
  createManualAgentPromptCandidate,
  deleteAgentEvaluationScenario,
  deleteAgentEvaluationRun,
  duplicateAgentEvaluationScenario,
  generateAgentEvaluationScenarios,
  getTestLabAgent,
  getAgentEvaluationSuite,
  getTestLabProviderManifest,
  ProviderManifest,
  rollbackAgentPromptVersion,
  startAgentEvaluationRun,
  updateAgentEvaluationScenario,
  updateAgentPromptCandidate,
} from "../../../services/api";
import { Provider } from "../../../types/strategies";
import { ProviderSettingsDraft } from "../../../types/strategyConfig";
import { buildDefaultProviderSettings } from "../../../utils/providerSettings";
import {
  cloneSelection,
  defaultInstructions,
  emptyScenarioPayload,
  formatPercent,
  getPreferredProviderDefinition,
  hasCriticalRiskText,
  preserveScenarioCallDirections,
  scenarioToPayload,
  splitCommaList,
  toApiProvider,
  PromptEditorState,
} from "./shared";

interface UseAgentTestLabOptions {
  uuid?: string;
  onMissingUuid: () => void;
}

export const useAgentTestLab = ({
  uuid,
  onMissingUuid,
}: UseAgentTestLabOptions) => {
  const [agent, setAgent] = useState<AgentDetail | null>(null);
  const [manifest, setManifest] = useState<ProviderManifest | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [scenarios, setScenarios] = useState<AgentEvaluationScenario[]>([]);
  const [runs, setRuns] = useState<AgentEvaluationRun[]>([]);
  const [promptCandidates, setPromptCandidates] = useState<
    AgentEvaluationPromptCandidate[]
  >([]);
  const [promptVersions, setPromptVersions] = useState<AgentPromptVersion[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"scenarios" | "runs" | "optimizer">(
    "scenarios"
  );
  const [scenarioFilter, setScenarioFilter] = useState<
    "all" | "failed" | "needs_review" | "passed" | "edge"
  >("all");
  const [scenarioSort, setScenarioSort] = useState<
    "recently-failed" | "accuracy" | "newest" | "oldest"
  >("recently-failed");
  const [editorSubTab, setEditorSubTab] = useState<
    "definition" | "run" | "verdict"
  >("definition");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [scenarioProvider, setScenarioProvider] =
    useState<ProviderSettingsDraft | null>(null);
  const [agentProvider, setAgentProvider] = useState<ProviderSettingsDraft | null>(null);
  const [userProvider, setUserProvider] = useState<ProviderSettingsDraft | null>(null);
  const [judgeProvider, setJudgeProvider] =
    useState<ProviderSettingsDraft | null>(null);
  const [optimizerProvider, setOptimizerProvider] =
    useState<ProviderSettingsDraft | null>(null);

  const [personaCount, setPersonaCount] = useState(3);
  const [casesPerPersona, setCasesPerPersona] = useState(2);
  const [personaMix, setPersonaMix] = useState<AgentEvaluationPersonaType[]>([
    "good",
    "bad",
    "edge",
  ]);
  const [languageMix, setLanguageMix] = useState("English, Hindi, Hinglish");
  const [includeLanguageSwitching, setIncludeLanguageSwitching] = useState(true);
  const [generationCallDirection, setGenerationCallDirection] =
    useState<AgentEvaluationCallDirection>("incoming");
  const [instructions, setInstructions] = useState(defaultInstructions);
  const [maxTurns, setMaxTurns] = useState(4);
  const [maxParallelScenarios, setMaxParallelScenarios] = useState(3);
  const [passThreshold, setPassThreshold] = useState(0.85);
  const [maxIterations, setMaxIterations] = useState(3);
  const [runtimeFields, setRuntimeFields] = useState<AgentRuntimeFields>({});

  const [isGenerating, setIsGenerating] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isSavingScenario, setIsSavingScenario] = useState(false);
  const [promptActionInFlight, setPromptActionInFlight] = useState(false);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [runPendingDelete, setRunPendingDelete] =
    useState<AgentEvaluationRun | null>(null);
  const [promptEditor, setPromptEditor] = useState<PromptEditorState>(null);

  const generateAbortRef = useRef<AbortController | null>(null);
  const runAbortRef = useRef<AbortController | null>(null);
  const optimizeAbortRef = useRef<AbortController | null>(null);

  const isAbortError = (err: any) =>
    err?.name === "CanceledError" ||
    err?.name === "AbortError" ||
    err?.code === "ERR_CANCELED" ||
    err?.message === "canceled";

  const llmProviders = manifest?.llm ?? [];
  const selectedScenario = scenarios.find(
    (scenario) => scenario.id === selectedScenarioId
  );
  const latestRun = runs[0];
  const latestBaselineRun = runs.find((run) => run.run_type === "baseline");
  const activeCandidate =
    promptCandidates.find((candidate) =>
      ["completed", "draft", "running"].includes(candidate.status)
    ) ?? promptCandidates[0];
  const comparisonRun = runs.find(
    (run) =>
      activeCandidate?.comparison_run_id && run.id === activeCandidate.comparison_run_id
  );
  const failedScenarios = scenarios.filter((scenario) => {
    const result = scenario.latest_result;
    return !!result && (result.verdict !== "pass" || result.critical_failure);
  });
  const needsReviewScenarios = scenarios.filter(
    (scenario) => scenario.latest_result?.verdict === "needs_review"
  );
  const passedScenarios = scenarios.filter(
    (scenario) =>
      scenario.latest_result?.verdict === "pass" &&
      !scenario.latest_result?.critical_failure
  );
  const filteredScenarios = (() => {
    switch (scenarioFilter) {
      case "failed":
        return failedScenarios;
      case "needs_review":
        return needsReviewScenarios;
      case "passed":
        return passedScenarios;
      case "edge":
        return scenarios.filter((scenario) => scenario.persona_type === "edge");
      case "all":
      default:
        return scenarios;
    }
  })();
  const visibleScenarios = (() => {
    const list = [...filteredScenarios];
    switch (scenarioSort) {
      case "accuracy": {
        return list.sort((a, b) => {
          const aScore = a.latest_result?.score ?? -1;
          const bScore = b.latest_result?.score ?? -1;
          return aScore - bScore;
        });
      }
      case "newest":
        return list.sort((a, b) => b.version - a.version);
      case "oldest":
        return list.sort((a, b) => a.version - b.version);
      case "recently-failed":
      default:
        return list.sort((a, b) => {
          const aFailed =
            a.latest_result &&
            (a.latest_result.verdict !== "pass" || a.latest_result.critical_failure)
              ? 0
              : 1;
          const bFailed =
            b.latest_result &&
            (b.latest_result.verdict !== "pass" || b.latest_result.critical_failure)
              ? 0
              : 1;
          if (aFailed !== bFailed) return aFailed - bFailed;
          return b.version - a.version;
        });
    }
  })();
  const fixesNeeded = failedScenarios.reduce(
    (sum, scenario) => sum + (scenario.latest_result?.fixes_needed.length ?? 0),
    0
  );
  const selectedFailedIds = failedScenarios.map((scenario) => scenario.id);
  const activeCandidateHasCriticalRisk =
    !!activeCandidate &&
    (activeCandidate.critical_failures.length > 0 ||
      hasCriticalRiskText(activeCandidate.risk_notes));
  const canAcceptCandidate =
    !!activeCandidate &&
    activeCandidate.status === "completed" &&
    !!activeCandidate.comparison_run_id &&
    !activeCandidateHasCriticalRisk;

  const optimizerVisible =
    (failedScenarios.length > 0 && !!latestBaselineRun) ||
    promptCandidates.some((candidate) =>
      ["running", "completed", "draft"].includes(candidate.status)
    );

  const hasUnreviewedCandidate = promptCandidates.some(
    (candidate) => candidate.status === "completed"
  );

  const previousAccuracy = runs[1]?.accuracy_score;

  const applyProviderDefaults = useCallback(
    (agentData: AgentDetail, manifestData: ProviderManifest) => {
      const definition = getPreferredProviderDefinition(manifestData.llm, "gemini");
      const defaultLlmSelection = definition
        ? {
            provider: definition.id as Provider,
            settings: buildDefaultProviderSettings(definition),
          }
        : null;
      setScenarioProvider(cloneSelection(defaultLlmSelection));
      setAgentProvider(cloneSelection(defaultLlmSelection));
      setUserProvider(cloneSelection(defaultLlmSelection));
      setJudgeProvider(cloneSelection(defaultLlmSelection));
      setOptimizerProvider(cloneSelection(defaultLlmSelection));

      const knownLanguages = [
        ...((agentData as any).supported_languages ?? []),
        (agentData as any).primary_language,
      ].filter(Boolean);
      if (knownLanguages.length > 0) {
        setLanguageMix(Array.from(new Set(knownLanguages)).join(", "));
      }
      setGenerationCallDirection(
        agentData.is_outbound ? "outbound" : "incoming"
      );
    },
    []
  );

  const applySuite = useCallback((suite: AgentEvaluationSuite) => {
    const suiteRuns = suite.runs ?? [];
    const suiteCandidates = suite.prompt_candidates ?? [];
    setCurrentPrompt(suite.current_prompt ?? "");
    setScenarios((current) =>
      preserveScenarioCallDirections(suite.scenarios ?? [], current)
    );
    setRuns(suiteRuns);
    setPromptCandidates(suiteCandidates);
    setPromptVersions(suite.prompt_versions ?? []);
    setIsRunning(suiteRuns.some((run) => run.status === "running" || run.status === "queued"));
    setIsOptimizing(suiteCandidates.some((candidate) => candidate.status === "running"));
    setSelectedScenarioId((current) => current ?? suite.scenarios?.[0]?.id ?? null);
    setSelectedIds((current) =>
      current.length > 0
        ? current
        : (suite.scenarios ?? []).map((scenario) => scenario.id)
    );
  }, []);

  const refreshSuite = useCallback(async () => {
    if (!uuid) return;
    const suite = await getAgentEvaluationSuite(uuid);
    applySuite(suite);
  }, [uuid, applySuite]);

  useEffect(() => {
    const loadData = async () => {
      if (!uuid) {
        onMissingUuid();
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const [agentData, manifestData, suiteData] = await Promise.all([
          getTestLabAgent(uuid),
          getTestLabProviderManifest(uuid),
          getAgentEvaluationSuite(uuid),
        ]);
        setAgent(agentData);
        setRuntimeFields(
          JSON.parse(JSON.stringify(agentData.fields ?? {})) as AgentRuntimeFields
        );
        setManifest(manifestData);
        applyProviderDefaults(agentData, manifestData);
        applySuite(suiteData);
        setNotice(null);
      } catch (loadError: any) {
        if (loadError instanceof AgentEvaluationUnavailableError) {
          setNotice("Evaluation backend is not available yet.");
        } else {
          setError(loadError.message || "Failed to load agent test lab");
        }
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [uuid, onMissingUuid, applyProviderDefaults, applySuite]);

  useEffect(() => {
    if (!isRunning) return;

    const intervalId = window.setInterval(() => {
      void refreshSuite();
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [isRunning, refreshSuite]);

  useEffect(() => {
    if (!isOptimizing && !promptActionInFlight) return;

    const intervalId = window.setInterval(() => {
      void refreshSuite();
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [isOptimizing, promptActionInFlight, refreshSuite]);

  const updateScenarioLocal = (
    scenarioId: string,
    patch: Partial<AgentEvaluationScenario>
  ) => {
    setScenarios((current) =>
      current.map((scenario) =>
        scenario.id === scenarioId ? { ...scenario, ...patch } : scenario
      )
    );
  };

  const toggleScenarioSelection = (scenarioId: string) => {
    setSelectedIds((current) =>
      current.includes(scenarioId)
        ? current.filter((id) => id !== scenarioId)
        : [...current, scenarioId]
    );
  };

  const togglePersonaType = (type: AgentEvaluationPersonaType) => {
    setPersonaMix((current) => {
      if (current.includes(type)) {
        return current.length === 1
          ? current
          : current.filter((item) => item !== type);
      }
      return [...current, type];
    });
  };

  const setRuntimeFieldValue = (key: string, value: string) => {
    setRuntimeFields((current) => ({
      ...current,
      [key]: Array.isArray(current[key])
        ? value.split(",").map((item) => item.trim()).filter(Boolean)
        : value,
    }));
  };

  const setRuntimeUserDetailValue = (key: string, value: string) => {
    setRuntimeFields((current) => ({
      ...current,
      USER_DETAILS: {
        ...(typeof current.USER_DETAILS === "object" &&
        !Array.isArray(current.USER_DETAILS)
          ? current.USER_DETAILS
          : {}),
        [key]: value,
      },
    }));
  };

  const resetRuntimeFields = () => {
    setRuntimeFields(
      JSON.parse(JSON.stringify(agent?.fields ?? {})) as AgentRuntimeFields
    );
  };

  const handleProviderDefaults = (
    setter: (selection: ProviderSettingsDraft | null) => void
  ) => {
    const definition = getPreferredProviderDefinition(llmProviders);
    setter(
      definition
        ? {
            provider: definition.id as Provider,
            settings: buildDefaultProviderSettings(definition),
          }
        : null
    );
  };

  const resetProvidersToDefaults = () => {
    if (!agent || !manifest) return;
    applyProviderDefaults(agent, manifest);
  };

  const handleGenerateScenarios = async () => {
    if (!uuid) return;
    const provider = toApiProvider(scenarioProvider);
    if (!provider) {
      setError("Choose a scenario provider in Test settings first.");
      return;
    }

    const controller = new AbortController();
    generateAbortRef.current = controller;

    try {
      setIsGenerating(true);
      setError(null);
      const response = await generateAgentEvaluationScenarios(
        uuid,
        {
          provider,
          persona_count: personaCount,
          test_cases_per_persona: casesPerPersona,
          max_parallel_generations: maxParallelScenarios,
          persona_mix: personaMix,
          languages: splitCommaList(languageMix),
          include_language_switching: includeLanguageSwitching,
          call_direction: generationCallDirection,
          instructions,
        },
        controller.signal
      );
      if (response.scenarios.length > 0) {
        const generatedScenarios = preserveScenarioCallDirections(
          response.scenarios,
          [],
          generationCallDirection
        );
        setScenarios((current) => [...current, ...generatedScenarios]);
        setSelectedIds((current) =>
          Array.from(
            new Set([...current, ...generatedScenarios.map((scenario) => scenario.id)])
          )
        );
        setSelectedScenarioId(generatedScenarios[0]?.id ?? selectedScenarioId);
      }
      const failedCount = response.failed_count ?? 0;
      const requestedCount =
        response.requested_count ?? personaCount * casesPerPersona;
      setNotice(
        response.scenarios.length === 0
          ? `Scenario generation started for ${requestedCount} slots. Refreshing as scenarios are saved.`
          : failedCount > 0
          ? `${response.scenarios.length} of ${requestedCount} scenarios generated. ${failedCount} slot${failedCount === 1 ? "" : "s"} timed out or failed.`
          : `${response.scenarios.length} scenarios generated and saved.`
      );
      if (response.scenarios.length === 0) {
        for (let delay = 3000; delay <= 90000; delay += 3000) {
          window.setTimeout(() => void refreshSuite(), delay);
        }
      } else {
        window.setTimeout(() => void refreshSuite(), 3000);
      }
    } catch (generateError: any) {
      if (isAbortError(generateError)) {
        setNotice("Generation cancelled. Server-side work may still finish; results were discarded.");
      } else {
        setError(generateError.message || "Failed to generate scenarios");
      }
    } finally {
      if (generateAbortRef.current === controller) {
        generateAbortRef.current = null;
      }
      setIsGenerating(false);
    }
  };

  const cancelGeneration = () => {
    generateAbortRef.current?.abort();
  };

  const handleAddScenario = async () => {
    if (!uuid) return;
    try {
      setError(null);
      const scenario = await createAgentEvaluationScenario(
        uuid,
        emptyScenarioPayload(splitCommaList(languageMix), generationCallDirection)
      );
      const scenarioWithDirection = preserveScenarioCallDirections(
        [scenario],
        [],
        generationCallDirection
      )[0];
      setScenarios((current) => [...current, scenarioWithDirection]);
      setSelectedIds((current) => [...current, scenarioWithDirection.id]);
      setSelectedScenarioId(scenarioWithDirection.id);
    } catch (addError: any) {
      setError(addError.message || "Failed to add scenario");
    }
  };

  const handleSaveScenario = async (
    payloadOverride?: AgentEvaluationScenarioPayload
  ) => {
    if (!uuid || !selectedScenario) return;
    try {
      setIsSavingScenario(true);
      setError(null);
      const payload = payloadOverride ?? scenarioToPayload(selectedScenario);
      const saved = await updateAgentEvaluationScenario(
        uuid,
        selectedScenario.id,
        payload
      );
      const savedWithDirection = preserveScenarioCallDirections(
        [saved],
        [{ ...selectedScenario, call_direction: payload.call_direction }]
      )[0];
      setScenarios((current) =>
        current.map((scenario) =>
          scenario.id === savedWithDirection.id ? savedWithDirection : scenario
        )
      );
      setNotice("Scenario saved.");
    } catch (saveError: any) {
      setError(saveError.message || "Failed to save scenario");
    } finally {
      setIsSavingScenario(false);
    }
  };

  const handleDuplicateScenario = async () => {
    if (!uuid || !selectedScenario) return;
    try {
      setError(null);
      const duplicate = await duplicateAgentEvaluationScenario(uuid, selectedScenario.id);
      const duplicateWithDirection = preserveScenarioCallDirections(
        [duplicate],
        [],
        selectedScenario.call_direction ?? generationCallDirection
      )[0];
      setScenarios((current) => [...current, duplicateWithDirection]);
      setSelectedIds((current) => [...current, duplicateWithDirection.id]);
      setSelectedScenarioId(duplicateWithDirection.id);
    } catch (duplicateError: any) {
      setError(duplicateError.message || "Failed to duplicate scenario");
    }
  };

  const handleDeleteScenario = async () => {
    if (!uuid || !selectedScenario) return;
    try {
      setError(null);
      await deleteAgentEvaluationScenario(uuid, selectedScenario.id);
      const remaining = scenarios.filter(
        (scenario) => scenario.id !== selectedScenario.id
      );
      setScenarios(remaining);
      setSelectedScenarioId(remaining[0]?.id ?? null);
      setSelectedIds((current) => current.filter((id) => id !== selectedScenario.id));
    } catch (deleteError: any) {
      setError(deleteError.message || "Failed to delete scenario");
    }
  };

  const runScenarioIds = async (
    scenarioIds: string[],
    signal?: AbortSignal
  ) => {
    if (!uuid) return null;
    const userModel = toApiProvider(userProvider);
    const judgeModel = toApiProvider(judgeProvider);
    const agentModel = toApiProvider(agentProvider);
    if (!agentModel || !userModel || !judgeModel) {
      setError("Choose agent, user simulator, and judge providers in Test settings first.");
      return null;
    }
    if (scenarioIds.length === 0) {
      setError("Select at least one scenario to run.");
      return null;
    }

    const scenariosForRun = scenarioIds
      .map((scenarioId) => scenarios.find((scenario) => scenario.id === scenarioId))
      .filter(Boolean) as AgentEvaluationScenario[];
    const syncedScenarios =
      scenariosForRun.length > 0
        ? await Promise.all(
            scenariosForRun.map((scenario) =>
              updateAgentEvaluationScenario(
                uuid,
                scenario.id,
                scenarioToPayload(scenario)
              )
            )
          )
        : [];
    const scenarioDefinitions = preserveScenarioCallDirections(
      syncedScenarios,
      scenariosForRun
    ).map((scenario) => ({
      id: scenario.id,
      version: scenario.version,
      ...scenarioToPayload(scenario),
    }));
    if (scenarioDefinitions.length > 0) {
      setScenarios((current) =>
        current.map((scenario) => {
          const synced = scenarioDefinitions.find((item) => item.id === scenario.id);
          return synced ? { ...scenario, ...synced } : scenario;
        })
      );
    }

    const run = await startAgentEvaluationRun(
      uuid,
      {
        agent_provider: agentModel,
        user_provider: userModel,
        judge_provider: judgeModel,
        scenario_ids: scenarioIds,
        scenario_definitions: scenarioDefinitions,
        max_turns: maxTurns,
        max_parallel_scenarios: maxParallelScenarios,
        pass_threshold: passThreshold,
        runtime_fields: runtimeFields,
      },
      signal
    );
    setRuns((current) => [run, ...current.filter((item) => item.id !== run.id)]);
    const resultByScenario = new Map(
      run.results.map((result) => [result.scenario_id, result])
    );
    setScenarios((current) =>
      current.map((scenario) =>
        resultByScenario.has(scenario.id)
          ? { ...scenario, latest_result: resultByScenario.get(scenario.id) ?? null }
          : scenario
      )
    );
    return run;
  };

  const handleRunSelected = async () => {
    const controller = new AbortController();
    runAbortRef.current = controller;
    try {
      setIsRunning(true);
      setError(null);
      const run = await runScenarioIds(selectedIds, controller.signal);
      if (run) {
        setNotice(
          run.status === "running" || run.status === "queued"
            ? "Run started. Results will update here as scenarios finish."
            : `Run completed with ${formatPercent(run.accuracy_score)} accuracy.`
        );
      }
    } catch (runError: any) {
      if (isAbortError(runError)) {
        setNotice("Run cancelled. In-flight provider calls may finish, but cancelled results are not saved.");
      } else {
        setError(runError.message || "Failed to run selected scenarios");
      }
    } finally {
      if (runAbortRef.current === controller) runAbortRef.current = null;
      void refreshSuite();
    }
  };

  const handleRerunFailed = async () => {
    setSelectedIds(selectedFailedIds);
    const controller = new AbortController();
    runAbortRef.current = controller;
    try {
      setIsRunning(true);
      setError(null);
      const run = await runScenarioIds(selectedFailedIds, controller.signal);
      if (run) {
        setNotice(
          run.status === "running" || run.status === "queued"
            ? "Failed-scenario rerun started. Results will update here as scenarios finish."
            : `Failed scenarios rerun. Accuracy: ${formatPercent(run.accuracy_score)}.`
        );
      }
    } catch (runError: any) {
      if (isAbortError(runError)) {
        setNotice("Rerun cancelled. In-flight provider calls may finish, but cancelled results are not saved.");
      } else {
        setError(runError.message || "Failed to rerun failed scenarios");
      }
    } finally {
      if (runAbortRef.current === controller) runAbortRef.current = null;
      void refreshSuite();
    }
  };

  const cancelRun = () => {
    runAbortRef.current?.abort();
  };

  const requestDeleteRun = (run: AgentEvaluationRun) => {
    setRunPendingDelete(run);
  };

  const cancelDeleteRun = () => {
    if (deletingRunId) return;
    setRunPendingDelete(null);
  };

  const confirmDeleteRun = async () => {
    const run = runPendingDelete;
    if (!uuid || !run) return;

    try {
      setDeletingRunId(run.id);
      setError(null);
      await deleteAgentEvaluationRun(uuid, run.id);
      setRuns((current) => current.filter((item) => item.id !== run.id));
      await refreshSuite();
      setRunPendingDelete(null);
      setNotice("Run deleted from Test Lab history.");
    } catch (deleteError: any) {
      setError(deleteError.message || "Failed to delete run");
    } finally {
      setDeletingRunId(null);
    }
  };

  const handleOptimizePrompt = async () => {
    if (!uuid || !latestBaselineRun) {
      setError("Run a baseline chat test before optimizing the prompt.");
      return;
    }
    const optimizerModel = toApiProvider(optimizerProvider);
    const agentModel = toApiProvider(agentProvider);
    const userModel = toApiProvider(userProvider);
    const judgeModel = toApiProvider(judgeProvider);
    if (!optimizerModel || !agentModel || !userModel || !judgeModel) {
      setError("Choose optimizer, agent, user simulator, and judge providers first.");
      return;
    }

    const controller = new AbortController();
    optimizeAbortRef.current = controller;
    try {
      setIsOptimizing(true);
      setError(null);
      setActiveTab("optimizer");
      setNotice(
        `Optimizer started. It will create a candidate, test the same failed scenarios, and update this tab while it runs.`
      );
      await createAgentPromptOptimization(
        uuid,
        {
          source_run_id: latestBaselineRun.id,
          optimizer_provider: optimizerModel,
          agent_provider: agentModel,
          user_provider: userModel,
          judge_provider: judgeModel,
          max_iterations: maxIterations,
          max_parallel_scenarios: maxParallelScenarios,
          pass_threshold: passThreshold,
          runtime_fields: runtimeFields,
        },
        controller.signal
      );
      await refreshSuite();
      setNotice("Prompt optimization started. This tab will update as the candidate is tested.");
    } catch (optimizeError: any) {
      if (isAbortError(optimizeError)) {
        setNotice("Optimization cancelled. Server-side iterations may still complete; results were discarded.");
      } else {
        setError(optimizeError.message || "Failed to optimize prompt");
      }
    } finally {
      if (optimizeAbortRef.current === controller) optimizeAbortRef.current = null;
      void refreshSuite();
    }
  };

  const cancelOptimization = () => {
    optimizeAbortRef.current?.abort();
  };

  const handleSavePromptEdit = async (prompt: string) => {
    if (!uuid) return;
    if (promptEditor?.mode === "candidate") {
      try {
        setPromptActionInFlight(true);
        const updated = await updateAgentPromptCandidate(uuid, promptEditor.candidateId, {
          proposed_prompt: prompt,
          rerun: false,
        });
        setPromptCandidates((current) =>
          current.map((candidate) =>
            candidate.id === updated.id ? updated : candidate
          )
        );
        setNotice("Candidate prompt saved. Rerun it before accepting.");
      } catch (promptError: any) {
        setError(promptError.message || "Failed to save candidate prompt");
      } finally {
        setPromptActionInFlight(false);
      }
      return;
    }

    if (!latestBaselineRun) {
      setError("Run a baseline first. Manual prompt edits need a comparison run before acceptance.");
      return;
    }

    try {
      setPromptActionInFlight(true);
      const candidate = await createManualAgentPromptCandidate(uuid, {
        source_run_id: latestBaselineRun.id,
        proposed_prompt: prompt,
      });
      setPromptCandidates((current) => [candidate, ...current]);
      setNotice("Manual prompt candidate created. Rerun it before accepting.");
    } catch (promptError: any) {
      setError(promptError.message || "Failed to create manual prompt candidate");
    } finally {
      setPromptActionInFlight(false);
    }
  };

  const handleRerunCandidate = async (candidate: AgentEvaluationPromptCandidate) => {
    if (!uuid) return;
    const agentModel = toApiProvider(agentProvider);
    const userModel = toApiProvider(userProvider);
    const judgeModel = toApiProvider(judgeProvider);
    if (!agentModel || !userModel || !judgeModel) {
      setError("Choose agent, user simulator, and judge providers first.");
      return;
    }

    try {
      setPromptActionInFlight(true);
      setError(null);
      setActiveTab("optimizer");
      setPromptCandidates((current) =>
        current.map((item) =>
          item.id === candidate.id ? { ...item, status: "running" } : item
        )
      );
      setNotice("Candidate rerun started. The comparison will update here as scenario results finish.");
      await updateAgentPromptCandidate(uuid, candidate.id, {
        proposed_prompt: candidate.proposed_prompt,
        agent_provider: agentModel,
        user_provider: userModel,
        judge_provider: judgeModel,
        max_parallel_scenarios: maxParallelScenarios,
        pass_threshold: passThreshold,
        runtime_fields: runtimeFields,
        rerun: true,
      });
      await refreshSuite();
      setNotice("Candidate rerun started. The comparison will update here as scenario results finish.");
    } catch (rerunError: any) {
      setError(rerunError.message || "Failed to rerun candidate");
    } finally {
      void refreshSuite();
      setPromptActionInFlight(false);
    }
  };

  const handleAcceptCandidate = async (candidate: AgentEvaluationPromptCandidate) => {
    if (!uuid) return;
    try {
      setPromptActionInFlight(true);
      setError(null);
      await acceptAgentPromptCandidate(uuid, candidate.id);
      const [agentData] = await Promise.all([getTestLabAgent(uuid), refreshSuite()]);
      setAgent(agentData);
      setNotice("New prompt accepted and applied to the agent.");
    } catch (acceptError: any) {
      setError(acceptError.message || "Failed to accept prompt candidate");
    } finally {
      setPromptActionInFlight(false);
    }
  };

  const handleRollback = async (version: AgentPromptVersion) => {
    if (!uuid) return;
    try {
      setPromptActionInFlight(true);
      setError(null);
      await rollbackAgentPromptVersion(uuid, version.id);
      const [agentData] = await Promise.all([getTestLabAgent(uuid), refreshSuite()]);
      setAgent(agentData);
      setNotice("Prompt rolled back to the selected accepted version.");
    } catch (rollbackError: any) {
      setError(rollbackError.message || "Failed to roll back prompt");
    } finally {
      setPromptActionInFlight(false);
    }
  };

  const summary = useMemo(
    () => ({
      scenarioCount: scenarios.length,
      selectedCount: selectedIds.length,
      latestAccuracy: latestRun?.accuracy_score,
      fixesNeeded,
    }),
    [scenarios.length, selectedIds.length, latestRun?.accuracy_score, fixesNeeded]
  );

  return {
    agent,
    currentPrompt,
    scenarios,
    runs,
    promptCandidates,
    promptVersions,
    selectedIds,
    selectedScenarioId,
    activeTab,
    scenarioFilter,
    scenarioSort,
    editorSubTab,
    settingsOpen,
    loading,
    error,
    notice,
    scenarioProvider,
    agentProvider,
    userProvider,
    judgeProvider,
    optimizerProvider,
    personaCount,
    casesPerPersona,
    personaMix,
    languageMix,
    includeLanguageSwitching,
    generationCallDirection,
    instructions,
    maxTurns,
    maxParallelScenarios,
    passThreshold,
    maxIterations,
    runtimeFields,
    isGenerating,
    isRunning,
    isOptimizing,
    isSavingScenario,
    promptActionInFlight,
    deletingRunId,
    runPendingDelete,
    promptEditor,
    llmProviders,
    selectedScenario,
    latestRun,
    latestBaselineRun,
    activeCandidate,
    activeCandidateHasCriticalRisk,
    comparisonRun,
    failedScenarios,
    needsReviewScenarios,
    passedScenarios,
    visibleScenarios,
    selectedFailedIds,
    canAcceptCandidate,
    optimizerVisible,
    hasUnreviewedCandidate,
    previousAccuracy,
    generationScenarioCount: personaCount * casesPerPersona,
    summary,
    setScenarioProvider,
    setAgentProvider,
    setUserProvider,
    setJudgeProvider,
    setOptimizerProvider,
    setPersonaCount,
    setCasesPerPersona,
    setLanguageMix,
    setIncludeLanguageSwitching,
    setGenerationCallDirection,
    setInstructions,
    setMaxTurns,
    setMaxParallelScenarios,
    setPassThreshold,
    setMaxIterations,
    setRuntimeFieldValue,
    setRuntimeUserDetailValue,
    resetRuntimeFields,
    setSelectedIds,
    setSelectedScenarioId,
    setActiveTab,
    setScenarioFilter,
    setScenarioSort,
    setEditorSubTab,
    setSettingsOpen,
    setPromptEditor,
    updateScenarioLocal,
    toggleScenarioSelection,
    togglePersonaType,
    handleProviderDefaults,
    resetProvidersToDefaults,
    handleGenerateScenarios,
    handleAddScenario,
    handleSaveScenario,
    handleDuplicateScenario,
    handleDeleteScenario,
    handleRunSelected,
    handleRerunFailed,
    requestDeleteRun,
    cancelDeleteRun,
    confirmDeleteRun,
    handleOptimizePrompt,
    handleSavePromptEdit,
    handleRerunCandidate,
    handleAcceptCandidate,
    handleRollback,
    cancelGeneration,
    cancelRun,
    cancelOptimization,
  };
};

export type UseAgentTestLab = ReturnType<typeof useAgentTestLab>;
